import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, ExternalLink, AlertTriangle, Clock, FileText } from 'lucide-react';
import api from '../../utils/api';
import { PAGE_WIDE, DataTable, SearchInput, Tabs, KpiTile, PersonAvatar, num, plural } from '../../components/ui';
import { ExpiryBadge, IconButton, expiryStatus, expirySortKey, docHref, docName } from './hrKit';

export default function StaffDocumentExpired() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get('/staff/documents/expired')
      .then((r) => { const d = r.data; setDocs(Array.isArray(d) ? d : (d.documents || d.data || [])); })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load expiring documents. Try again."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const expired = docs.filter((d) => expiryStatus(d.expiry_date).state === 'expired');
  const soon = docs.filter((d) => expiryStatus(d.expiry_date).state === 'soon');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs
      .filter((d) => filter === 'all' || expiryStatus(d.expiry_date).state === (filter === 'expired' ? 'expired' : 'soon'))
      .filter((d) => !q || `${d.staff_name || ''} ${d.category || ''} ${docName(d)}`.toLowerCase().includes(q))
      .sort((a, b) => expirySortKey(a.expiry_date) - expirySortKey(b.expiry_date));
  }, [docs, filter, search]);

  const columns = [
    { key: 'staff', header: 'Staff', sortable: true, sortValue: (d) => d.staff_name || '', render: (d) => (
      <span className="flex items-center gap-2.5">
        <PersonAvatar id={d.staff_id} firstName={d.staff_first_name} lastName={d.staff_last_name} name={d.staff_name} size="sm" />
        {d.staff_id
          ? <Link to={`/staff/${d.staff_id}/documents`} className="font-medium text-slate-900 hover:text-primary-700 hover:underline">{d.staff_name || 'Unknown'}</Link>
          : <span className="font-medium text-slate-700">{d.staff_name || 'Not linked to anyone'}</span>}
      </span>
    ) },
    { key: 'doc', header: 'Document', sortable: true, sortValue: (d) => docName(d).toLowerCase(), render: (d) => <span className="font-medium text-slate-800">{docName(d) || '—'}</span> },
    { key: 'category', header: 'Category', render: (d) => (d.category && d.category !== 'Shared' ? d.category : <span className="text-slate-400">—</span>) },
    { key: 'expiry', header: 'Expiry', sortable: true, sortValue: (d) => expirySortKey(d.expiry_date), render: (d) => <ExpiryBadge date={d.expiry_date} /> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: (d) => (docHref(d) ? <IconButton label={`Open ${docName(d)}`} icon={ExternalLink} href={docHref(d)} target="_blank" rel="noreferrer" /> : null) },
  ];

  return (
    <div className={PAGE_WIDE}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiTile label="Expired" value={loading ? '…' : num(expired.length)} alert={!loading && expired.length > 0} accent="slate" icon={ShieldAlert} />
        <KpiTile label="Expiring in 30 days" value={loading ? '…' : num(soon.length)} accent="amber" icon={Clock} />
        <KpiTile label="Total listed" value={loading ? '…' : num(docs.length)} accent="slate" icon={FileText} />
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center">
          <Tabs variant="segmented" size="sm" ariaLabel="Expiry" value={filter} onChange={setFilter} className="self-start"
            tabs={[{ id: 'all', label: 'All', count: docs.length }, { id: 'expired', label: 'Expired', count: expired.length }, { id: 'soon', label: 'Expiring', count: soon.length }]} />
          <SearchInput value={search} onChange={setSearch} placeholder="Search staff or document" className="w-full sm:ml-auto sm:w-64" />
        </div>
        <DataTable
          label="Expired and expiring staff documents"
          columns={columns}
          rows={rows}
          loading={loading}
          error={loadError}
          onRetry={load}
          empty={docs.length === 0
            ? { icon: AlertTriangle, title: 'Nothing expired or expiring', hint: 'Every staff document with an expiry date is valid for more than 30 days.' }
            : { icon: AlertTriangle, title: 'Nothing matches', hint: `${plural(docs.length, 'document')} listed under other filters.`, action: <button type="button" onClick={() => { setFilter('all'); setSearch(''); }} className="btn-secondary btn-sm">Show all</button> }}
        />
      </div>
    </div>
  );
}
